import { Product } from '@smartphone-tycoon/shared-core';
import { MarketSegment } from '@smartphone-tycoon/shared-core';
import { MarketSimulatorService } from './marketSimulatorService';
import { EconomicCycleService } from './economicCycleService';
import { CompetitorAIService } from './competitorAIService';
import { MarketCapacityService } from './marketCapacityService';
import { roundToThousandths } from '@smartphone-tycoon/shared-core';
import { MARKET_FORMULAS } from '../../config/gameFormulas';
import { SALES_CONSTANTS, ECONOMIC_CONSTANTS, TIME_CONSTANTS, MARKET_CONSTANTS, MARKET_SEGMENTS } from '@/config/gameConstants';
import type { GameDate } from '../../types/events';

export interface SalesBreakdown {
  dailySales: number;
  // New normalized demand share model fields
  segmentDailyDemand: number;   // Segment-level demand (same for all products in segment)
  demandShare: number;           // Normalized share (0-1, sums to ~1 across segment)
  attractivenessScore: number;   // Raw attractiveness score before normalization
  // Deprecated fields (kept for backward compatibility, set to 1.0 or mirror new values)
  baseDemand: number;            // = segmentDailyDemand (backward compat)
  priceMultiplier: number;       // = 1.0 (absorbed into demandShare)
  qualityMultiplier: number;     // = 1.0 (absorbed into demandShare)
  competitionMultiplier: number; // = 1.0 (removed, competition in demandShare)
  marketMultiplier: number;
  timeMultiplier: number;
  factors: {
    // Segment-level factors
    marketSegmentSize: number;
    segmentPenetration: number;
    marketGrowthRate: number;
    activeProductsInSegment: number;
    // Product pricing factors
    price: number;
    optimalPrice: number;
    priceElasticity: number;
    // New attractiveness breakdown
    priceScore: number;              // Price contribution to attractiveness (0-1)
    qualityScore: number;            // Quality contribution to attractiveness (0-1)
    brandScore: number;              // Brand/reputation contribution (0.2-1.0)
    productAge: number;              // Months since launch
    marketTimingBonus: number;       // Era-based bonus (0-0.2)
    randomFactor: number;            // Small random component (0.97-1.03)
    totalAttractivenessInSegment: number; // Sum of all product scores in segment
    // Existing factors (kept for reference)
    techScore: number;
    marketAppeal: number;
    userScore: number;
    marketShare: number;
    competitionIntensity: number;
    economicFactor: number;
    seasonalFactor: number;
    eventFactor: number;
    lifecycleStage: string;
    lifecycleMultiplier: number;
    adoptionMultiplier: number;
  };
}

// Attractiveness breakdown for debug/analysis
interface AttractivenessBreakdown {
  priceScore: number;
  qualityScore: number;
  brandScore: number;
  marketTimingBonus: number;
  ageFactor: number;
  randomFactor: number;
}

interface SegmentParameters {
  baseSize: number;
  priceElasticity: number;
  optimalPriceRange: [number, number];
  techSensitivity: number;
  brandLoyalty: number;
}

interface LifecycleStage {
  name: string;
  minMonths: number;
  maxMonths: number;
  salesMultiplierRange: [number, number];
}

export class SalesCalculationService {
  private static instance: SalesCalculationService;
  private currentYear: number = 2000; // Track current year for market evolution
  private randomSeed: number | null = null; // Optional seed for deterministic random

  /**
   * Set a random seed for deterministic behavior (useful for testing)
   * Pass null to use Math.random() (default production behavior)
   */
  public setRandomSeed(seed: number | null): void {
    this.randomSeed = seed;
  }

  /**
   * Get a random number, using the seed if set for deterministic behavior
   */
  private getRandom(): number {
    if (this.randomSeed !== null) {
      // Simple seeded random using linear congruential generator
      this.randomSeed = (this.randomSeed * 1103515245 + 12345) & 0x7fffffff;
      return (this.randomSeed / 0x7fffffff);
    }
    return Math.random();
  }

  private segmentParameters: Record<string, SegmentParameters> = {
    [MARKET_SEGMENTS.names.BUDGET]: {
      baseSize: MARKET_SEGMENTS.sizes.budget,
      priceElasticity: MARKET_SEGMENTS.priceElasticity.budget,
      optimalPriceRange: [MARKET_SEGMENTS.priceRanges.budget.min, MARKET_SEGMENTS.priceRanges.budget.max],
      techSensitivity: MARKET_SEGMENTS.techSensitivity.budget,
      brandLoyalty: MARKET_SEGMENTS.brandLoyalty.budget
    },
    [MARKET_SEGMENTS.names.BUDGET_CONSCIOUS]: {
      baseSize: MARKET_SEGMENTS.sizes.budget,
      priceElasticity: MARKET_SEGMENTS.priceElasticity.budget,
      optimalPriceRange: [MARKET_SEGMENTS.priceRanges.budget.min, MARKET_SEGMENTS.priceRanges.budget.max],
      techSensitivity: MARKET_SEGMENTS.techSensitivity.budget,
      brandLoyalty: MARKET_SEGMENTS.brandLoyalty.budget
    },
    [MARKET_SEGMENTS.names.MAINSTREAM]: {
      baseSize: MARKET_SEGMENTS.sizes.mainstream,
      priceElasticity: MARKET_SEGMENTS.priceElasticity.mainstream,
      optimalPriceRange: [MARKET_SEGMENTS.priceRanges.mainstream.min, MARKET_SEGMENTS.priceRanges.mainstream.max],
      techSensitivity: MARKET_SEGMENTS.techSensitivity.mainstream,
      brandLoyalty: MARKET_SEGMENTS.brandLoyalty.mainstream
    },
    [MARKET_SEGMENTS.names.MAINSTREAM_USERS]: {
      baseSize: MARKET_SEGMENTS.sizes.mainstream,
      priceElasticity: MARKET_SEGMENTS.priceElasticity.mainstream,
      optimalPriceRange: [MARKET_SEGMENTS.priceRanges.mainstream.min, MARKET_SEGMENTS.priceRanges.mainstream.max],
      techSensitivity: MARKET_SEGMENTS.techSensitivity.mainstream,
      brandLoyalty: MARKET_SEGMENTS.brandLoyalty.mainstream
    },
    [MARKET_SEGMENTS.names.PREMIUM]: {
      baseSize: MARKET_SEGMENTS.sizes.premium,
      priceElasticity: MARKET_SEGMENTS.priceElasticity.premium,
      optimalPriceRange: [MARKET_SEGMENTS.priceRanges.premium.min, MARKET_SEGMENTS.priceRanges.premium.max],
      techSensitivity: MARKET_SEGMENTS.techSensitivity.premium,
      brandLoyalty: MARKET_SEGMENTS.brandLoyalty.premium
    },
    [MARKET_SEGMENTS.names.PREMIUM_ENTHUSIASTS]: {
      baseSize: MARKET_SEGMENTS.sizes.premium,
      priceElasticity: MARKET_SEGMENTS.priceElasticity.premium,
      optimalPriceRange: [MARKET_SEGMENTS.priceRanges.premium.min, MARKET_SEGMENTS.priceRanges.premium.max],
      techSensitivity: MARKET_SEGMENTS.techSensitivity.premium,
      brandLoyalty: MARKET_SEGMENTS.brandLoyalty.premium
    },
    [MARKET_SEGMENTS.names.ENTERPRISE]: {
      baseSize: MARKET_SEGMENTS.sizes.enterprise,
      priceElasticity: MARKET_SEGMENTS.priceElasticity.enterprise,
      optimalPriceRange: [MARKET_SEGMENTS.priceRanges.enterprise.min, MARKET_SEGMENTS.priceRanges.enterprise.max],
      techSensitivity: MARKET_SEGMENTS.techSensitivity.enterprise,
      brandLoyalty: MARKET_SEGMENTS.brandLoyalty.enterprise
    },
    [MARKET_SEGMENTS.names.ENTERPRISE_USERS]: {
      baseSize: MARKET_SEGMENTS.sizes.enterprise,
      priceElasticity: MARKET_SEGMENTS.priceElasticity.enterprise,
      optimalPriceRange: [MARKET_SEGMENTS.priceRanges.enterprise.min, MARKET_SEGMENTS.priceRanges.enterprise.max],
      techSensitivity: MARKET_SEGMENTS.techSensitivity.enterprise,
      brandLoyalty: MARKET_SEGMENTS.brandLoyalty.enterprise
    }
  };

  private lifecycleStages: LifecycleStage[] = [
    { 
      name: SALES_CONSTANTS.lifecycleStages.LAUNCH, 
      minMonths: 0, 
      maxMonths: SALES_CONSTANTS.lifecycleDurations.launch, 
      salesMultiplierRange: [SALES_CONSTANTS.lifecycleMultipliers.launch.min, SALES_CONSTANTS.lifecycleMultipliers.launch.max] 
    },
    { 
      name: SALES_CONSTANTS.lifecycleStages.GROWTH, 
      minMonths: SALES_CONSTANTS.lifecycleDurations.launch, 
      maxMonths: SALES_CONSTANTS.lifecycleDurations.launch + SALES_CONSTANTS.lifecycleDurations.growth, 
      salesMultiplierRange: [SALES_CONSTANTS.lifecycleMultipliers.growth.min, SALES_CONSTANTS.lifecycleMultipliers.growth.max] 
    },
    { 
      name: SALES_CONSTANTS.lifecycleStages.MATURITY, 
      minMonths: SALES_CONSTANTS.lifecycleDurations.launch + SALES_CONSTANTS.lifecycleDurations.growth, 
      maxMonths: SALES_CONSTANTS.lifecycleDurations.launch + SALES_CONSTANTS.lifecycleDurations.growth + SALES_CONSTANTS.lifecycleDurations.maturity, 
      salesMultiplierRange: [SALES_CONSTANTS.lifecycleMultipliers.maturity.min, SALES_CONSTANTS.lifecycleMultipliers.maturity.max] 
    },
    { 
      name: SALES_CONSTANTS.lifecycleStages.DECLINE, 
      minMonths: SALES_CONSTANTS.lifecycleDurations.launch + SALES_CONSTANTS.lifecycleDurations.growth + SALES_CONSTANTS.lifecycleDurations.maturity, 
      maxMonths: SALES_CONSTANTS.lifecycleDurations.decline, 
      salesMultiplierRange: [SALES_CONSTANTS.lifecycleMultipliers.decline.min, SALES_CONSTANTS.lifecycleMultipliers.decline.max] 
    }
  ];

  private economicPhaseModifiers = ECONOMIC_CONSTANTS.phaseModifiers;

  private constructor() {}

  static getInstance(): SalesCalculationService {
    if (!SalesCalculationService.instance) {
      SalesCalculationService.instance = new SalesCalculationService();
    }
    return SalesCalculationService.instance;
  }

  calculateDailySales(
    product: Product,
    marketSegment: MarketSegment,
    currentDate: GameDate,
    companyReputation: number,
    companyMarketShare: number,
    availableInventory?: number
  ): SalesBreakdown {
    // Update current year for market evolution
    this.currentYear = currentDate.year;

    // Apply year-based market evolution to segment
    const evolvedSegment = this.applyMarketEvolution(marketSegment, this.currentYear);

    // ============================================
    // STEP 1: Calculate segment-level daily demand
    // (Same for ALL products in this segment - no company/product factors)
    // ============================================
    const segmentDailyDemand = this.calculateSegmentDailyDemand(evolvedSegment);

    // ============================================
    // STEP 2: Calculate segment-level modifiers
    // (Applied equally to all products in segment)
    // ============================================
    const marketMultiplier = this.calculateMarketMultiplier(evolvedSegment, currentDate);
    const timeMultiplier = this.calculateTimeMultiplier(product, currentDate);

    // Adjusted segment demand (before product-level allocation)
    const adjustedSegmentDemand = segmentDailyDemand * marketMultiplier * timeMultiplier;

    // ============================================
    // STEP 3: Get all active products for share calculation
    // ============================================
    const allProducts = this.getAllActiveProducts();
    if (!allProducts.find(p => p.id === product.id)) {
      allProducts.push(product);
    }

    // ============================================
    // STEP 4: Calculate normalized demand share
    // (Product-specific allocation based on attractiveness)
    // ============================================
    const { share: demandShare, totalScore, productScore, breakdown } =
      this.calculateNormalizedDemandShare(
        product,
        evolvedSegment,
        allProducts,
        companyReputation,
        currentDate
      );

    // ============================================
    // STEP 5: Calculate final uncapped sales
    // NEW FORMULA: dailySales = segmentDemand * marketMultiplier * timeMultiplier * demandShare
    // ============================================
    let uncappedDailySales = Math.round(adjustedSegmentDemand * demandShare);

    // Guard against NaN
    if (isNaN(uncappedDailySales)) {
      console.error(`[SalesCalculation] uncappedDailySales is NaN for ${product.name}, using 0`);
      uncappedDailySales = 0;
    }

    // ============================================
    // STEP 6: Apply constraints
    // ============================================

    // Market capacity constraints
    const marketCapacityService = MarketCapacityService.getInstance();
    const constrainedSales = marketCapacityService.constrainDailySales(
      marketSegment.name,
      uncappedDailySales,
      currentDate
    );

    // Inventory constraint
    let dailySales = constrainedSales;
    if (availableInventory !== undefined && availableInventory >= 0) {
      dailySales = Math.min(dailySales, Math.floor(availableInventory));
    }

    // ============================================
    // DEBUG LOGGING
    // ============================================
    if (import.meta.env.DEV) {
      // console.log('[SalesCalculation] NEW Formula: dailySales = segmentDemand * market * time * demandShare');
      // console.log('[SalesCalculation] Segment-Level:', {
      //   segment: evolvedSegment.name,
      //   segmentDailyDemand: Math.round(segmentDailyDemand),
      //   marketMultiplier: roundToThousandths(marketMultiplier),
      //   timeMultiplier: roundToThousandths(timeMultiplier),
      //   adjustedSegmentDemand: Math.round(adjustedSegmentDemand)
      // });
      // console.log('[SalesCalculation] Product-Level:', {
      //   product: product.name,
      //   attractivenessScore: roundToThousandths(productScore),
      //   totalSegmentScore: roundToThousandths(totalScore),
      //   demandShare: roundToThousandths(demandShare),
      //   priceScore: roundToThousandths(breakdown.priceScore),
      //   qualityScore: roundToThousandths(breakdown.qualityScore),
      //   brandScore: roundToThousandths(breakdown.brandScore)
      // });
      // console.log('[SalesCalculation] Final:', {
      //   uncappedDailySales,
      //   constrainedSales,
      //   dailySales
      // });
    }

    // ============================================
    // BUILD RESPONSE (with backward compatibility)
    // ============================================
    const monthsSinceLaunch = this.getMonthsSinceLaunch(product, currentDate);
    const lifecycleStage = this.getLifecycleStage(monthsSinceLaunch);
    const segmentParams = this.segmentParameters[evolvedSegment.name];

    return {
      dailySales,
      // New fields
      segmentDailyDemand: roundToThousandths(adjustedSegmentDemand),
      demandShare: roundToThousandths(demandShare),
      attractivenessScore: roundToThousandths(productScore),
      // Backward compatibility fields (deprecated)
      baseDemand: roundToThousandths(adjustedSegmentDemand), // Mirrors segmentDailyDemand
      priceMultiplier: 1.0,      // Absorbed into demandShare
      qualityMultiplier: 1.0,    // Absorbed into demandShare
      competitionMultiplier: 1.0, // Removed - competition handled in demandShare
      marketMultiplier: roundToThousandths(marketMultiplier),
      timeMultiplier: roundToThousandths(timeMultiplier),
      factors: {
        // Segment factors
        marketSegmentSize: evolvedSegment.size || segmentParams?.baseSize || 0,
        segmentPenetration: MARKET_CONSTANTS.annualPhonePurchaseRate,
        marketGrowthRate: evolvedSegment.growthRate || 1.0,
        activeProductsInSegment: this.getActiveProductsInSegment(evolvedSegment.name),
        // Product pricing factors
        price: product.price,
        optimalPrice: segmentParams?.optimalPriceRange
          ? (segmentParams.optimalPriceRange[0] + segmentParams.optimalPriceRange[1]) / 2
          : 500,
        priceElasticity: segmentParams?.priceElasticity || 1.0,
        // New attractiveness breakdown
        priceScore: roundToThousandths(breakdown.priceScore),
        qualityScore: roundToThousandths(breakdown.qualityScore),
        brandScore: roundToThousandths(breakdown.brandScore),
        productAge: monthsSinceLaunch,
        marketTimingBonus: roundToThousandths(breakdown.marketTimingBonus),
        randomFactor: roundToThousandths(breakdown.randomFactor),
        totalAttractivenessInSegment: roundToThousandths(totalScore),
        // Existing factors (kept for reference)
        techScore: product.techScore || 50,
        marketAppeal: product.marketAppeal || 50,
        userScore: this.calculateUserScore(product, companyReputation),
        marketShare: companyMarketShare,
        competitionIntensity: this.calculateCompetitionIntensity(product, evolvedSegment),
        economicFactor: this.getEconomicFactor(evolvedSegment.name),
        seasonalFactor: this.getSeasonalFactor(currentDate),
        eventFactor: this.getEventFactor(evolvedSegment.name),
        lifecycleStage: lifecycleStage.name,
        lifecycleMultiplier: this.getLifecycleMultiplier(product, currentDate),
        adoptionMultiplier: this.getAdoptionMultiplier(product, currentDate)
      }
    };
  }

  /**
   * Calculate segment-level daily demand (pure market size, NO product/company factors)
   * This represents the total daily demand for the entire segment, shared among all products.
   * Company reputation and product factors are handled in calculateNormalizedDemandShare.
   */
  private calculateSegmentDailyDemand(marketSegment: MarketSegment): number {
    const segmentParams = this.segmentParameters[marketSegment.name];
    if (!segmentParams) {
      console.warn(`Unknown market segment: ${marketSegment.name}`);
      // Return minimum segment demand instead of 0 to prevent zero sales
      return MARKET_CONSTANTS.minimumSegmentDemand;
    }

    const marketSize = marketSegment.size || segmentParams.baseSize;
    const penetration = MARKET_CONSTANTS.annualPhonePurchaseRate;

    // Get year-based growth rate
    const yearBasedGrowth = this.getYearBasedGrowthRate(this.currentYear);
    const growthRate = marketSegment.growthRate || yearBasedGrowth;

    // Calculate annual market demand for the entire segment
    const annualDemand = marketSize * penetration * growthRate;

    // Convert to daily demand for the segment
    const dailySegmentDemand = annualDemand / TIME_CONSTANTS.daysPerYear;

    // NO reputation penalty here - that belongs in demand share calculation
    // Return the pure segment demand (minimum to prevent edge cases)
    return Math.max(MARKET_CONSTANTS.minimumSegmentDemand, dailySegmentDemand);
  }
  
  private getYearBasedGrowthRate(year: number): number {
    const growthRates = MARKET_SEGMENTS.growthRatesByEra;
    
    if (year <= growthRates.featurePhone.years[1]) {
      return 1 + growthRates.featurePhone.rate;
    } else if (year <= growthRates.earlySmartphone.years[1]) {
      return 1 + growthRates.earlySmartphone.rate;
    } else if (year <= growthRates.mainstream.years[1]) {
      return 1 + growthRates.mainstream.rate;
    } else {
      return 1 + growthRates.mature.rate;
    }
  }

  private calculatePriceMultiplier(product: Product, marketSegment: MarketSegment): number {
    const segmentParams = this.segmentParameters[marketSegment.name];
    if (!segmentParams) return 1;

    const price = product.price;
    const [minOptimal, maxOptimal] = segmentParams.optimalPriceRange;
    const optimalPrice = (minOptimal + maxOptimal) / 2;
    
    // Adjust price elasticity based on year - consumers become less price sensitive over time
    let k = segmentParams.priceElasticity;
    if (this.currentYear >= 2015) {
      k *= 0.7; // 30% less price sensitive in mature market
    } else if (this.currentYear >= 2010) {
      k *= 0.85; // 15% less price sensitive during mainstream adoption
    } else if (this.currentYear >= 2007) {
      k *= 0.95; // 5% less price sensitive during early smartphone era
    }

    const priceMultiplier = MARKET_FORMULAS.getPriceMultiplier(price, optimalPrice, k);
    
    return roundToThousandths(Math.max(MARKET_CONSTANTS.priceMultiplierMin, Math.min(MARKET_CONSTANTS.priceMultiplierMax, priceMultiplier)));
  }

  private calculateQualityMultiplier(product: Product, companyReputation: number): number {
    const techScore = product.techScore || MARKET_CONSTANTS.defaultTechScore;
    const marketAppeal = product.marketAppeal || MARKET_CONSTANTS.defaultMarketAppeal;
    
    const componentScore = this.calculateComponentScore(product);
    const innovationScore = this.calculateInnovationScore(product);
    const brandScore = companyReputation;
    const satisfactionScore = this.calculateCustomerSatisfaction(product);
    
    const userScore = (
      componentScore * SALES_CONSTANTS.componentScoreWeights.components +
      innovationScore * SALES_CONSTANTS.componentScoreWeights.innovation +
      brandScore * SALES_CONSTANTS.componentScoreWeights.brand +
      satisfactionScore * SALES_CONSTANTS.componentScoreWeights.satisfaction
    );

    const qualityMultiplier = MARKET_FORMULAS.getQualityMultiplier(techScore, marketAppeal, userScore);
    
    return roundToThousandths(Math.max(MARKET_CONSTANTS.qualityMultiplierMin, Math.min(MARKET_CONSTANTS.qualityMultiplierMax, qualityMultiplier)));
  }

  private calculateCompetitionMultiplier(
    product: Product,
    marketSegment: MarketSegment,
    companyMarketShare: number
  ): number {
    // Market share should have a significant impact on sales
    // 0% market share = very low multiplier, 50% = high multiplier
    const marketShareFactor = Math.pow(companyMarketShare / MARKET_CONSTANTS.marketShareDivisor, MARKET_CONSTANTS.marketSharePower);
    const competitionIntensity = this.calculateCompetitionIntensity(product, marketSegment);
    
    // Base multiplier increases with market share, decreases with competition
    const baseMultiplier = MARKET_CONSTANTS.baseCompetitionMultiplier + (marketShareFactor * MARKET_CONSTANTS.competitionMultiplierRange);
    const competitionMultiplier = MARKET_FORMULAS.getCompetitionMultiplier(baseMultiplier, competitionIntensity);
    
    return roundToThousandths(Math.max(MARKET_CONSTANTS.competitionMultiplierMin, Math.min(MARKET_CONSTANTS.competitionMultiplierMax, competitionMultiplier)));
  }

  private calculateMarketMultiplier(marketSegment: MarketSegment, currentDate: GameDate): number {
    const economicFactor = this.getEconomicFactor(marketSegment.name);
    const seasonalFactor = this.getSeasonalFactor(currentDate);
    const eventFactor = this.getEventFactor(marketSegment.name);
    
    const marketMultiplier = economicFactor * seasonalFactor * eventFactor;
    
    return roundToThousandths(Math.max(MARKET_CONSTANTS.marketMultiplierMin, Math.min(MARKET_CONSTANTS.marketMultiplierMax, marketMultiplier)));
  }

  private calculateTimeMultiplier(product: Product, currentDate: GameDate): number {
    const lifecycleMultiplier = this.getLifecycleMultiplier(product, currentDate);
    const adoptionMultiplier = this.getAdoptionMultiplier(product, currentDate);
    
    const timeMultiplier = lifecycleMultiplier * adoptionMultiplier;
    // console.log('[calculateTimeMultiplier] lifecycleMultiplier: ' + lifecycleMultiplier + ' adoptionMultiplier: ' + adoptionMultiplier)
    
    return roundToThousandths(Math.max(MARKET_CONSTANTS.timeMultiplierMin, Math.min(MARKET_CONSTANTS.timeMultiplierMax, timeMultiplier)));
  }

  private calculateComponentScore(product: Product): number {
    if (!product.designConfig?.components) return MARKET_CONSTANTS.defaultTechScore;
    
    // For now, use the tech score directly since we don't have component details
    // In the future, we could look up component specs from a component database
    return Math.min(100, product.techScore || MARKET_CONSTANTS.defaultTechScore);
  }

  private calculateInnovationScore(product: Product): number {
    const baseInnovation = MARKET_CONSTANTS.baseInnovation;
    // Use tech score as a proxy for innovation
    const techBonus = Math.max(0, (product.techScore || MARKET_CONSTANTS.defaultTechScore) - MARKET_CONSTANTS.defaultTechScore) * MARKET_CONSTANTS.innovationTechBonus;
    
    return Math.min(100, baseInnovation + techBonus);
  }

  private calculateCustomerSatisfaction(product: Product): number {
    const baseSatisfaction = MARKET_CONSTANTS.baseSatisfaction;
    const qualityBonus = (product.techScore || MARKET_CONSTANTS.defaultTechScore) > MARKET_CONSTANTS.qualityBonusThreshold ? MARKET_CONSTANTS.qualityBonus : 0;
    const priceBonus = product.price < MARKET_CONSTANTS.budgetPriceThreshold ? MARKET_CONSTANTS.budgetBonus : MARKET_CONSTANTS.premiumPenalty;
    
    return Math.max(0, Math.min(100, baseSatisfaction + qualityBonus + priceBonus));
  }

  private calculateCompetitionIntensity(product: Product, marketSegment: MarketSegment): number {
    // Get competitor products from the service
    const competitorProducts = CompetitorAIService.getInstance().getCompetitorProducts();
    
    let totalIntensity = 0;
    let competitorCount = 0;
    
    // Calculate intensity based on competitor products in the same segment
    competitorProducts.forEach((products) => {
      const segmentProducts = products.filter(p => 
        this.getProductSegment(p) === marketSegment.name
      );
      
      if (segmentProducts.length > 0) {
        competitorCount++;
        const overlap = this.calculateProductOverlap(product, segmentProducts);
        totalIntensity += overlap;
      }
    });
    
    // Normalize intensity based on number of competitors
    return competitorCount > 0 ? Math.min(1.0, totalIntensity / competitorCount) : MARKET_CONSTANTS.defaultCompetitionIntensity;
  }

  private calculateProductOverlap(product: Product, competitorProducts: Product[]): number {
    const relevantProducts = competitorProducts.filter(p => 
      Math.abs(p.price - product.price) < MARKET_CONSTANTS.priceSimilarityDivisor
    );
    
    if (relevantProducts.length === 0) return 0;
    
    const avgOverlap = relevantProducts.reduce((sum, p) => {
      const priceSimilarity = 1 - Math.abs(p.price - product.price) / MARKET_CONSTANTS.priceSimilarityDivisor;
      const techSimilarity = 1 - Math.abs((p.techScore || MARKET_CONSTANTS.defaultTechScore) - (product.techScore || MARKET_CONSTANTS.defaultTechScore)) / MARKET_CONSTANTS.techSimilarityDivisor;
      return sum + (priceSimilarity * 0.5 + techSimilarity * 0.5);
    }, 0) / relevantProducts.length;
    
    return avgOverlap;
  }

  private getEconomicFactor(segmentName: string): number {
    const economicCycle = EconomicCycleService.getInstance().getEconomicCycle();
    const phaseModifier = this.economicPhaseModifiers[economicCycle.phase];
    
    if (!phaseModifier) return 1.0;
    
    let factor = phaseModifier.demandModifier;
    
    if (segmentName === 'Premium' && phaseModifier.premiumImpact !== 0) {
      factor *= (1 + phaseModifier.premiumImpact);
    }
    
    return factor;
  }

  private getSeasonalFactor(currentDate: GameDate): number {
    const month = currentDate.month - 1; // GameDate months are 1-12, array is 0-11
    return SALES_CONSTANTS.monthlySeasonalFactors[month] || SALES_CONSTANTS.defaultEventMultiplier;
  }

  private getEventFactor(segmentName: string): number {
    const events = MarketSimulatorService.getInstance().getActiveEvents();
    
    if (events.length === 0) return SALES_CONSTANTS.defaultEventMultiplier;
    
    // Since MarketEvent doesn't have targetSegments or impact.sales,
    // we'll use the effects property to determine impact
    const totalFactor = events.reduce((factor, event) => {
      // Check if this event affects this segment
      const segmentEffect = event.effects.segments.find(
        s => s.segmentId === segmentName.toLowerCase()
      );
      
      if (segmentEffect) {
        return factor * (1 + segmentEffect.impact);
      }
      
      // Apply general market size effect if no specific segment effect
      return factor * (1 + event.effects.marketSize * MARKET_CONSTANTS.economicImpactMultiplier);
    }, 1.0);
    
    return Math.max(0.5, Math.min(2.0, totalFactor));
  }

  private getLifecycleMultiplier(product: Product, currentDate: GameDate): number {
    const monthsSinceLaunch = this.getMonthsSinceLaunch(product, currentDate);

    // Guard against NaN
    if (isNaN(monthsSinceLaunch)) {
      console.error(`[getLifecycleMultiplier] monthsSinceLaunch is NaN for ${product.name}, using 0`);
      return 1.0; // Return neutral multiplier
    }

    const stage = this.getLifecycleStage(monthsSinceLaunch);

    const [min, max] = stage.salesMultiplierRange;
    const progress = this.getStageProgress(monthsSinceLaunch, stage);

    // console.debug('[getLifecycleMultiplier] monthsSinceLaunch: ' + monthsSinceLaunch + ' stage: ' + stage.name + ' min: ' + min + ' max: ' + max + ' progress: ' + progress)

    // Guard against NaN in progress
    if (isNaN(progress)) {
      console.error(`[getLifecycleMultiplier] progress is NaN for ${product.name}, using 0.5`);
      return (min + max) / 2;
    }

    if (stage.name === 'Growth') {
      return min + (max - min) * progress;
    } else if (stage.name === 'Decline') {
      return max - (max - min) * progress;
    } else {
      return (min + max) / 2;
    }
  }

  private getAdoptionMultiplier(product: Product, currentDate: GameDate): number {
    const monthsSinceLaunch = this.getMonthsSinceLaunch(product, currentDate);

    // Guard against NaN
    if (isNaN(monthsSinceLaunch)) {
      console.error(`[getAdoptionMultiplier] monthsSinceLaunch is NaN for ${product.name}, using 0`);
      return MARKET_CONSTANTS.adoptionCurveBase; // Return base multiplier
    }

    const adoptionCurve = MARKET_FORMULAS.getAdoptionMultiplier(monthsSinceLaunch);

    // Guard against NaN in adoptionCurve
    if (isNaN(adoptionCurve)) {
      console.error(`[getAdoptionMultiplier] adoptionCurve is NaN for ${product.name}, using 0`);
      return MARKET_CONSTANTS.adoptionCurveBase;
    }

    // console.debug('[getAdoptionMultiplier] monthsSinceLaunch: ' + monthsSinceLaunch + ' adoptionCurve: ' + adoptionCurve)

    return MARKET_CONSTANTS.adoptionCurveBase + (MARKET_CONSTANTS.adoptionCurveBase * adoptionCurve);
  }

  private getMonthsSinceLaunch(product: Product, currentDate: GameDate): number {
    if (!product.metadata?.releaseDate) {
      console.warn(`[getMonthsSinceLaunch] No releaseDate for product ${product.name}`);
      return 0;
    }

    const releaseDate = product.metadata.releaseDate;
    // console.debug(`[getMonthsSinceLaunch] Product ${product.name} releaseDate:`, releaseDate);

    // Validate releaseDate structure
    if (!releaseDate || typeof releaseDate !== 'object') {
      console.error(`[getMonthsSinceLaunch] Invalid releaseDate structure for ${product.name}:`, releaseDate);
      return 0;
    }

    // Check if releaseDate has the expected properties
    if (typeof releaseDate.year !== 'number' || typeof releaseDate.month !== 'number') {
      console.error(`[getMonthsSinceLaunch] releaseDate missing year or month for ${product.name}:`, releaseDate);
      // Try to handle Date object or timestamp
      if (releaseDate instanceof Date) {
        const year = releaseDate.getFullYear();
        const month = releaseDate.getMonth() + 1;
        const yearsDiff = currentDate.year - year;
        const monthsDiff = (currentDate.month - month) + (yearsDiff * 12);
        return Math.max(0, monthsDiff);
      } else if (typeof releaseDate === 'number') {
        // Handle timestamp
        const date = new Date(releaseDate);
        const year = date.getFullYear();
        const month = date.getMonth() + 1;
        const yearsDiff = currentDate.year - year;
        const monthsDiff = (currentDate.month - month) + (yearsDiff * 12);
        return Math.max(0, monthsDiff);
      }
      return 0;
    }

    // Calculate months difference between two GameDates
    const yearsDiff = currentDate.year - releaseDate.year;
    const monthsDiff = (currentDate.month - releaseDate.month) + (yearsDiff * 12);

    // Guard against NaN
    if (isNaN(monthsDiff)) {
      console.error(`[getMonthsSinceLaunch] Calculated NaN for ${product.name}. currentDate:`, currentDate, 'releaseDate:', releaseDate);
      return 0;
    }

    return Math.max(0, monthsDiff);
  }

  private getLifecycleStage(monthsSinceLaunch: number): LifecycleStage {
    for (const stage of this.lifecycleStages) {
      if (monthsSinceLaunch >= stage.minMonths && monthsSinceLaunch < stage.maxMonths) {
        return stage;
      }
    }
    return this.lifecycleStages[this.lifecycleStages.length - 1];
  }

  private getStageProgress(monthsSinceLaunch: number, stage: LifecycleStage): number {
    if (stage.maxMonths === Infinity) {
      return Math.min(1.0, (monthsSinceLaunch - stage.minMonths) / SALES_CONSTANTS.lifecycleDurations.declineProgressDivisor);
    }
    const duration = stage.maxMonths - stage.minMonths;
    const progress = (monthsSinceLaunch - stage.minMonths) / duration;
    return Math.max(0, Math.min(1, progress));
  }

  private getActiveProductsInSegment(segmentName: string): number {
    const competitorProducts = CompetitorAIService.getInstance().getCompetitorProducts();
    let activeProducts = 1; // Start with 1 for the player's product
    
    competitorProducts.forEach((products) => {
      activeProducts += products.filter(p => {
        // CompetitorProduct uses 'lifecycle' instead of 'status'
        const isActive = p.lifecycle ? 
          SALES_CONSTANTS.activeLifecycleStages.includes(p.lifecycle) : 
          p.status === SALES_CONSTANTS.productionStatus;
        return this.getProductSegment(p) === segmentName && isActive;
      }).length;
    });
    
    return activeProducts;
  }
  
  private getAllActiveProducts(): Product[] {
    // This is a simplified version - in a real implementation, 
    // you would get player products from the product manager
    // and competitor products from the competitor AI service
    const allProducts: Product[] = [];
    
    // Get competitor products
    const competitorProducts = CompetitorAIService.getInstance().getCompetitorProducts();
    competitorProducts.forEach((products) => {
      products.forEach(p => {
        const isActive = p.lifecycle ? 
          SALES_CONSTANTS.activeLifecycleStages.includes(p.lifecycle) : 
          p.status === SALES_CONSTANTS.productionStatus;
        if (isActive) {
          allProducts.push(p as Product);
        }
      });
    });
    
    return allProducts;
  }

  public getProductSegment(product: Product): string {
    const price = product.price;
    
    if (price <= MARKET_SEGMENTS.priceThresholds.budget) return MARKET_SEGMENTS.names.BUDGET;
    if (price <= MARKET_SEGMENTS.priceThresholds.mainstream) return MARKET_SEGMENTS.names.MAINSTREAM;
    if (price <= MARKET_SEGMENTS.priceThresholds.premium) return MARKET_SEGMENTS.names.PREMIUM;
    return MARKET_SEGMENTS.names.ENTERPRISE;
  }

  private getDetailedFactors(
    product: Product,
    marketSegment: MarketSegment,
    currentDate: GameDate,
    companyReputation: number,
    companyMarketShare: number
  ): SalesBreakdown['factors'] {
    const segmentParams = this.segmentParameters[marketSegment.name];
    const monthsSinceLaunch = this.getMonthsSinceLaunch(product, currentDate);
    const lifecycleStage = this.getLifecycleStage(monthsSinceLaunch);
    
    return {
      marketSegmentSize: marketSegment.size || segmentParams?.baseSize || 0,
      segmentPenetration: 0.3,
      marketGrowthRate: marketSegment.growthRate || 1.0,
      activeProductsInSegment: this.getActiveProductsInSegment(marketSegment.name),
      price: product.price,
      optimalPrice: segmentParams ? (segmentParams.optimalPriceRange[0] + segmentParams.optimalPriceRange[1]) / 2 : 500,
      priceElasticity: segmentParams?.priceElasticity || 1.0,
      techScore: product.techScore || 50,
      marketAppeal: product.marketAppeal || 50,
      userScore: this.calculateUserScore(product, companyReputation),
      marketShare: companyMarketShare,
      competitionIntensity: this.calculateCompetitionIntensity(product, marketSegment),
      economicFactor: this.getEconomicFactor(marketSegment.name),
      seasonalFactor: this.getSeasonalFactor(currentDate),
      eventFactor: this.getEventFactor(marketSegment.name),
      lifecycleStage: lifecycleStage.name,
      lifecycleMultiplier: this.getLifecycleMultiplier(product, currentDate),
      adoptionMultiplier: this.getAdoptionMultiplier(product, currentDate)
    };
  }

  private calculateUserScore(product: Product, companyReputation: number): number {
    const componentScore = this.calculateComponentScore(product);
    const innovationScore = this.calculateInnovationScore(product);
    const brandScore = companyReputation;
    const satisfactionScore = this.calculateCustomerSatisfaction(product);
    
    return (
      componentScore * 0.25 +
      innovationScore * 0.25 +
      brandScore * 0.25 +
      satisfactionScore * 0.25
    );
  }

  batchCalculateSales(
    products: Product[],
    currentDate: GameDate,
    companyReputation: number,
    companyMarketShare: number,
    inventoryMap?: Map<string, number>
  ): Map<string, SalesBreakdown> {
    const salesMap = new Map<string, SalesBreakdown>();
    
    products.forEach(product => {
      // Only calculate sales for products that are:
      // 1. In released status AND
      // 2. Have inventory available (or inventory check will happen in calculateDailySales)
      // console.debug(`[SalesCalculation] Processing ${product.name}: status=${product.status}`);

      if (product.status === 'released') {
        const availableInventory = inventoryMap?.get(product.id);

        // Skip if no inventory available
        if (availableInventory !== undefined && availableInventory <= 0) {
          console.log(`[SalesCalculation] Skipping ${product.name} - no inventory available`);
          return;
        }

        const segment = this.getMarketSegmentForProduct(product);
        const breakdown = this.calculateDailySales(
          product,
          segment,
          currentDate,
          companyReputation,
          companyMarketShare,
          availableInventory
        );
        // console.log(`[SalesCalculation] ${product.name}: calculated ${breakdown.dailySales} daily sales`);
        salesMap.set(product.id, breakdown);
      }
    });
    
    return salesMap;
  }

  private getMarketSegmentForProduct(product: Product): MarketSegment {
    const segmentName = this.getProductSegment(product);
    const marketSize = MarketSimulatorService.getInstance().getMarketSize();
    
    // Find the segment from the market size data
    const existingSegment = marketSize.segments.find(s => 
      s.name.includes(segmentName) || s.id === segmentName.toLowerCase()
    );
    
    if (existingSegment) return existingSegment;
    
    // Create a default segment if not found
    const segmentParams = this.segmentParameters[segmentName];
    return {
      id: segmentName.toLowerCase(),
      name: segmentName,
      size: segmentParams?.baseSize || 100_000_000,
      priceElasticity: segmentParams?.priceElasticity || 1.0,
      techSensitivity: segmentParams?.techSensitivity || 0.8,
      brandLoyalty: segmentParams?.brandLoyalty || 0.6,
      growthRate: 1.0,
      demographics: {
        ageRange: [18, 65] as [number, number],
        incomeRange: [20000, 100000] as [number, number],
        techAdoption: 'mainstream'
      }
    };
  }

  /**
   * Apply year-based market evolution to segment
   */
  private applyMarketEvolution(baseSegment: MarketSegment, year: number): MarketSegment {
    const evolution = MARKET_SEGMENTS.marketEvolution;
    let multiplier = 1.0;
    let techAdoptionMultiplier = 1.0;
    
    // Determine which era we're in and interpolate if needed
    if (year <= evolution.featurePhoneEra.endYear) {
      // Feature phone era
      const segmentKey = this.getSegmentKey(baseSegment.name);
      multiplier = evolution.featurePhoneEra.segmentMultipliers[segmentKey] || 1.0;
    } else if (year <= evolution.smartphoneIntroduction.endYear) {
      // Smartphone introduction - interpolate between feature phone and smartphone era
      const progress = (year - evolution.featurePhoneEra.endYear) / 
                      (evolution.smartphoneIntroduction.endYear - evolution.featurePhoneEra.endYear);
      const segmentKey = this.getSegmentKey(baseSegment.name);
      const startMultiplier = evolution.featurePhoneEra.segmentMultipliers[segmentKey] || 1.0;
      const endMultiplier = evolution.smartphoneIntroduction.segmentMultipliers[segmentKey] || 1.0;
      multiplier = startMultiplier + (endMultiplier - startMultiplier) * progress;
      
      // Technology adoption starts affecting preferences
      techAdoptionMultiplier = 1.0 + progress * 0.2;
    } else if (year <= evolution.mainstreamAdoption.endYear) {
      // Mainstream adoption - interpolate
      const progress = (year - evolution.smartphoneIntroduction.endYear) / 
                      (evolution.mainstreamAdoption.endYear - evolution.smartphoneIntroduction.endYear);
      const segmentKey = this.getSegmentKey(baseSegment.name);
      const startMultiplier = evolution.smartphoneIntroduction.segmentMultipliers[segmentKey] || 1.0;
      const endMultiplier = evolution.mainstreamAdoption.segmentMultipliers[segmentKey] || 1.0;
      multiplier = startMultiplier + (endMultiplier - startMultiplier) * progress;
      
      // Technology becomes more important
      techAdoptionMultiplier = 1.2 + progress * 0.3;
    } else {
      // Market maturity
      const segmentKey = this.getSegmentKey(baseSegment.name);
      multiplier = evolution.marketMaturity.segmentMultipliers[segmentKey] || 1.0;
      techAdoptionMultiplier = 1.5;
    }
    
    // Create evolved segment with adjusted size and characteristics
    const evolvedSegment: MarketSegment = {
      ...baseSegment,
      size: Math.floor(baseSegment.size * multiplier),
      techSensitivity: Math.min(2.0, baseSegment.techSensitivity * techAdoptionMultiplier),
      // Adjust price elasticity based on market maturity
      priceElasticity: year >= 2010 ? baseSegment.priceElasticity * 0.9 : baseSegment.priceElasticity
    };
    
    return evolvedSegment;
  }
  
  /**
   * Get segment key for evolution multipliers
   */
  private getSegmentKey(segmentName: string): string {
    const normalizedName = segmentName.toLowerCase();
    if (normalizedName.includes('budget')) return 'budget';
    if (normalizedName.includes('mainstream')) return 'mainstream';
    if (normalizedName.includes('premium')) return 'premium';
    if (normalizedName.includes('enterprise')) return 'enterprise';
    return 'mainstream'; // default
  }
  
  /**
   * Calculate product competitiveness score
   */
  private calculateProductCompetitiveness(
    product: Product,
    segment: MarketSegment,
    year: number
  ): number {
    console.log(`[CompetitivenessCalc] Product: ${product.name}, Segment name: '${segment.name}'`);

    let score = 0;

    // Tech score contribution - MUCH more important in later years
    let techImportance = 0.2; // Base importance
    if (year >= 2007) techImportance = 0.3;      // iPhone era begins
    if (year >= 2010) techImportance = 0.5;      // Mainstream smartphone adoption
    if (year >= 2015) techImportance = 0.6;      // Tech maturity, features matter most

    const techContribution = (product.techScore / 100) * techImportance;
    score += techContribution;

    // Price-value ratio - adjusted for segment
    // Try to find segment params with normalized name
    let segmentParams = this.segmentParameters[segment.name];

    // If not found, try to find by price-based segment
    if (!segmentParams) {
      const priceBasedSegment = this.getProductSegment(product);
      segmentParams = this.segmentParameters[priceBasedSegment];
      console.log(`[CompetitivenessCalc] Using price-based segment: ${priceBasedSegment}`);
    }

    console.log(`[CompetitivenessCalc] segmentParams found: ${segmentParams ? 'YES' : 'NO'}`);
    console.log(`[CompetitivenessCalc] Available segment keys: ${Object.keys(this.segmentParameters).join(', ')}`);

    // Safely calculate optimal price with fallback
    let optimalPrice = 500; // Default fallback
    if (segmentParams?.optimalPriceRange) {
      optimalPrice = (segmentParams.optimalPriceRange[0] + segmentParams.optimalPriceRange[1]) / 2;
    }
    
    // Calculate price competitiveness based on segment
    let priceScore = 0;
    const priceDiff = Math.abs(product.price - optimalPrice) / optimalPrice;

    if (segment.name.toLowerCase().includes('budget')) {
      // Budget segment: Lower price is always better
      priceScore = product.price < optimalPrice ? 1.0 : Math.max(0, 1 - priceDiff);
    } else if (segment.name.toLowerCase().includes('premium')) {
      // Premium segment: Price within range is good, too low is bad
      const minPremiumPrice = segmentParams?.optimalPriceRange?.[0] || 800;
      if (product.price < minPremiumPrice) {
        priceScore = 0.5; // Too cheap for premium
      } else {
        priceScore = Math.max(0, 1 - priceDiff * 0.5);
      }
    } else {
      // Mainstream: Sweet spot pricing matters most
      priceScore = Math.max(0, 1 - priceDiff);
    }

    // Guard against NaN
    if (isNaN(priceScore)) {
      console.warn(`[CompetitivenessCalc] priceScore is NaN for ${product.name}, using default 0.5`);
      priceScore = 0.5;
    }
    
    // Price importance decreases over time as tech becomes more important
    const priceImportance = year >= 2010 ? 0.25 : 0.35;
    score += priceScore * priceImportance;
    
    // Market timing bonus
    if (year >= 2007 && year <= 2012) {
      if (segment.name.toLowerCase().includes('premium') && product.techScore > 75) {
        score += 0.2; // Early smartphone premium advantage
      } else if (segment.name.toLowerCase().includes('mainstream') && product.techScore > 65) {
        score += 0.15; // Mainstream smartphone adoption bonus
      }
    }
    
    // Brand/company bonus (established products get slight advantage)
    if (product.metadata?.monthsSinceLaunch && product.metadata.monthsSinceLaunch > 3) {
      score += 0.1; // Established product bonus
    }

    // Quality and user satisfaction - use techScore as proxy if qualityScore doesn't exist
    const qualityValue = product.qualityScore || product.techScore || 50;
    const qualityScore = (qualityValue / 100) * 0.15;
    score += qualityScore;

    // Final NaN check
    if (isNaN(score)) {
      console.error(`[CompetitivenessCalc] Final score is NaN for ${product.name}, using default 0.5`);
      return 0.5;
    }

    return Math.max(0.1, Math.min(1.5, score)); // Allow for scores > 1 for exceptional products
  }
  
  /**
   * Calculate how well a product fits a segment
   */
  private calculateSegmentFit(product: Product, segment: MarketSegment): number {
    const segmentName = segment.name.toLowerCase();
    let fit = 0.5; // Base fit

    if (segmentName.includes('budget')) {
      // Budget segment values low price
      if (product.price <= 300) fit += 0.3;
      if (product.price <= 200) fit += 0.2;
    } else if (segmentName.includes('mainstream')) {
      // Mainstream values balance
      if (product.price >= 400 && product.price <= 700) fit += 0.3;
      if (product.techScore >= 60 && product.techScore <= 80) fit += 0.2;
    } else if (segmentName.includes('premium')) {
      // Premium values high tech and quality
      if (product.price >= 800) fit += 0.2;
      if (product.techScore >= 80) fit += 0.3;
    }

    return Math.min(1.0, fit);
  }

  /**
   * Calculate a product's attractiveness score for demand share allocation.
   * This score combines price fit, quality, brand reputation, market timing, and product age.
   * All products in a segment compete for demand share based on their attractiveness.
   */
  private calculateAttractivenessScore(
    product: Product,
    segment: MarketSegment,
    companyReputation: number,
    currentDate: GameDate
  ): { score: number; breakdown: AttractivenessBreakdown } {
    const weights = MARKET_CONSTANTS.attractivenessWeights;
    const ageFactorConfig = MARKET_CONSTANTS.ageFactor;
    const randomFactorConfig = MARKET_CONSTANTS.randomFactor;
    const segmentParams = this.segmentParameters[segment.name] ||
                          this.segmentParameters[this.getProductSegment(product)];

    // 1. Price Score (0-1): How well price matches segment optimal range
    let optimalPrice = 500; // Default fallback
    if (segmentParams?.optimalPriceRange) {
      optimalPrice = (segmentParams.optimalPriceRange[0] + segmentParams.optimalPriceRange[1]) / 2;
    }
    const priceDiff = Math.abs(product.price - optimalPrice) / optimalPrice;
    const priceScore = Math.max(0, Math.min(1, 1 - priceDiff));

    // 2. Quality Score (0-1): Tech score + market appeal combined
    const techScore = (product.techScore || MARKET_CONSTANTS.defaultTechScore) / 100;
    const appealScore = (product.marketAppeal || MARKET_CONSTANTS.defaultMarketAppeal) / 100;
    const qualityScore = techScore * 0.6 + appealScore * 0.4;

    // 3. Brand Score: Company reputation effect
    // New companies (low reputation) still compete, just with lower brand score
    const brandScore = MARKET_CONSTANTS.brandScoreBase +
      (Math.min(100, companyReputation) / 100) * MARKET_CONSTANTS.brandScoreScale;

    // 4. Market Timing Bonus: Era-based advantages
    let marketTimingBonus = 0;
    if (this.currentYear >= 2007 && this.currentYear <= 2012) {
      // Smartphone boom era bonuses
      if (segment.name.toLowerCase().includes('premium') && (product.techScore || MARKET_CONSTANTS.defaultTechScore) > 75) {
        marketTimingBonus = MARKET_CONSTANTS.marketTimingBonus.premium;
      } else if (segment.name.toLowerCase().includes('mainstream') && (product.techScore || MARKET_CONSTANTS.defaultTechScore) > 65) {
        marketTimingBonus = MARKET_CONSTANTS.marketTimingBonus.mainstream;
      }
    }

    // 5. Product Age Factor: Lifecycle position
    const monthsSinceLaunch = this.getMonthsSinceLaunch(product, currentDate);
    let ageFactor = 1.0;
    if (monthsSinceLaunch < ageFactorConfig.launchMonths) {
      // Launch phase: ramp up
      ageFactor = ageFactorConfig.launchStart +
        (monthsSinceLaunch / ageFactorConfig.launchMonths) * ageFactorConfig.launchRampUp;
    } else if (monthsSinceLaunch < ageFactorConfig.launchMonths + ageFactorConfig.growthMonths) {
      // Growth phase: peak bonus
      const growthProgress = (monthsSinceLaunch - ageFactorConfig.launchMonths) / ageFactorConfig.growthMonths;
      ageFactor = 1.0 + Math.min(ageFactorConfig.growthPeak, growthProgress * ageFactorConfig.growthPeak);
    } else if (monthsSinceLaunch > ageFactorConfig.matureMonths) {
      // Decline phase: gradual reduction
      ageFactor = Math.max(
        ageFactorConfig.declineFloor,
        ageFactorConfig.declineBase - (monthsSinceLaunch - ageFactorConfig.matureMonths) * ageFactorConfig.declineRate
      );
    }

    // 6. Small Random Factor: Prevents deterministic outcomes
    // Uses seeded random if set for testing, otherwise Math.random()
    const randomFactor = randomFactorConfig.base + this.getRandom() * randomFactorConfig.range;

    // Weighted combination
    const rawScore = (
      priceScore * weights.price +
      qualityScore * weights.quality +
      brandScore * weights.brand +
      marketTimingBonus +
      ageFactor * weights.productAge
    ) * randomFactor;

    // Minimum score to prevent zero demand share
    const minShare = MARKET_CONSTANTS.minimumDemandShare;

    return {
      score: Math.max(minShare, rawScore),
      breakdown: {
        priceScore,
        qualityScore,
        brandScore,
        marketTimingBonus,
        ageFactor,
        randomFactor
      }
    };
  }

  /**
   * Estimate competitor reputation based on their product metrics
   */
  private estimateCompetitorReputation(product: Product): number {
    const techFactor = (product.techScore || 50) / 100;
    // Established competitors have higher reputation (30-80 range)
    return Math.min(80, 30 + techFactor * 50);
  }

  /**
   * Calculate normalized demand share for a product.
   * Shares across all products in a segment sum to approximately 1.0.
   * This ensures competition redistributes demand rather than destroying it.
   */
  private calculateNormalizedDemandShare(
    product: Product,
    segment: MarketSegment,
    allProducts: Product[],
    companyReputation: number,
    currentDate: GameDate
  ): { share: number; totalScore: number; productScore: number; breakdown: AttractivenessBreakdown } {
    // Get all active products in this segment
    const segmentName = segment.name || this.getProductSegment(product);
    const segmentProducts = allProducts.filter(p => {
      const pSegment = this.getProductSegment(p);
      return (pSegment === segmentName || pSegment === this.getProductSegment(product)) &&
             p.status === 'released';
    });

    // Include current product if not in list
    if (!segmentProducts.find(p => p.id === product.id) && product.status === 'released') {
      segmentProducts.push(product);
    }

    // If no products in segment, this product gets 100% share
    if (segmentProducts.length === 0) {
      const { score, breakdown } = this.calculateAttractivenessScore(
        product, segment, companyReputation, currentDate
      );
      return { share: 1.0, totalScore: score, productScore: score, breakdown };
    }

    // Calculate attractiveness scores for all products
    const scores = new Map<string, { score: number; breakdown: AttractivenessBreakdown }>();
    let totalScore = 0;

    for (const p of segmentProducts) {
      // For player product, use actual reputation
      // For competitor products, estimate reputation based on their metrics
      const rep = p.id === product.id ? companyReputation :
                  this.estimateCompetitorReputation(p);

      const result = this.calculateAttractivenessScore(p, segment, rep, currentDate);
      scores.set(p.id, result);
      totalScore += result.score;
    }

    const productResult = scores.get(product.id);
    const productScore = productResult?.score || (MARKET_CONSTANTS.minimumDemandShare || 0.05);
    const breakdown = productResult?.breakdown || {
      priceScore: 0, qualityScore: 0, brandScore: 0,
      marketTimingBonus: 0, ageFactor: 1, randomFactor: 1
    };

    // Normalize: product's share of total attractiveness
    const normalizedShare = totalScore > 0 ? productScore / totalScore : 1.0;

    // Apply minimum share constraint
    const minShare = MARKET_CONSTANTS.minimumDemandShare || 0.05;
    const finalShare = Math.max(minShare, normalizedShare);

    return {
      share: finalShare,
      totalScore,
      productScore,
      breakdown
    };
  }

  /**
   * Calculate demand share based on competitiveness (legacy method, kept for backward compatibility)
   * @deprecated Use calculateNormalizedDemandShare instead
   */
  private calculateDemandShare(
    product: Product,
    segment: MarketSegment,
    allProducts: Product[],
    companyMarketShare: number
  ): number {
    const year = this.currentYear;
    const segmentProducts = allProducts.filter(p =>
      this.getProductSegment(p) === segment.name &&
      p.status === 'released'
    );

    if (segmentProducts.length === 0) return 1.0;

    // Calculate competitiveness scores for all products
    const scores = new Map<string, number>();
    let totalScore = 0;

    segmentProducts.forEach(p => {
      const score = this.calculateProductCompetitiveness(p, segment, year);
      scores.set(p.id, score);
      totalScore += score;
    });

    const productScore = scores.get(product.id) || 0;

    // Combine market share impact with competitiveness
    const marketShareWeight = MARKET_CONSTANTS.demandDistribution.marketShareImpact;
    const competitivenessWeight = MARKET_CONSTANTS.demandDistribution.productCompetitiveness;

    const marketShareComponent = companyMarketShare * marketShareWeight;
    const competitivenessComponent = totalScore > 0 ?
      (productScore / totalScore) * competitivenessWeight : 0;

    // Add small random factor to prevent zero sales
    const randomComponent = this.getRandom() * MARKET_CONSTANTS.demandDistribution.randomFactor;

    return marketShareComponent + competitivenessComponent + randomComponent;
  }

  /**
   * Reset the service to initial state
   */
  public reset(): void {
    // console.log('💰 Sales Calculation Service: Resetting to initial state');

    // Reset current year
    this.currentYear = 2000;

    // Reset random seed (restore non-deterministic behavior)
    this.randomSeed = null;
    
    // Reset segment parameters to default values
    this.segmentParameters = {
      [MARKET_SEGMENTS.names.BUDGET]: {
        baseSize: MARKET_SEGMENTS.sizes.budget,
        priceElasticity: MARKET_SEGMENTS.priceElasticity.budget,
        optimalPriceRange: [MARKET_SEGMENTS.priceRanges.budget.min, MARKET_SEGMENTS.priceRanges.budget.max],
        techSensitivity: MARKET_SEGMENTS.techSensitivity.budget,
        brandLoyalty: MARKET_SEGMENTS.brandLoyalty.budget
      },
      [MARKET_SEGMENTS.names.BUDGET_CONSCIOUS]: {
        baseSize: MARKET_SEGMENTS.sizes.budget,
        priceElasticity: MARKET_SEGMENTS.priceElasticity.budget,
        optimalPriceRange: [MARKET_SEGMENTS.priceRanges.budget.min, MARKET_SEGMENTS.priceRanges.budget.max],
        techSensitivity: MARKET_SEGMENTS.techSensitivity.budget,
        brandLoyalty: MARKET_SEGMENTS.brandLoyalty.budget
      },
      [MARKET_SEGMENTS.names.MAINSTREAM]: {
        baseSize: MARKET_SEGMENTS.sizes.mainstream,
        priceElasticity: MARKET_SEGMENTS.priceElasticity.mainstream,
        optimalPriceRange: [MARKET_SEGMENTS.priceRanges.mainstream.min, MARKET_SEGMENTS.priceRanges.mainstream.max],
        techSensitivity: MARKET_SEGMENTS.techSensitivity.mainstream,
        brandLoyalty: MARKET_SEGMENTS.brandLoyalty.mainstream
      },
      [MARKET_SEGMENTS.names.MAINSTREAM_USERS]: {
        baseSize: MARKET_SEGMENTS.sizes.mainstream,
        priceElasticity: MARKET_SEGMENTS.priceElasticity.mainstream,
        optimalPriceRange: [MARKET_SEGMENTS.priceRanges.mainstream.min, MARKET_SEGMENTS.priceRanges.mainstream.max],
        techSensitivity: MARKET_SEGMENTS.techSensitivity.mainstream,
        brandLoyalty: MARKET_SEGMENTS.brandLoyalty.mainstream
      },
      [MARKET_SEGMENTS.names.PREMIUM]: {
        baseSize: MARKET_SEGMENTS.sizes.premium,
        priceElasticity: MARKET_SEGMENTS.priceElasticity.premium,
        optimalPriceRange: [MARKET_SEGMENTS.priceRanges.premium.min, MARKET_SEGMENTS.priceRanges.premium.max],
        techSensitivity: MARKET_SEGMENTS.techSensitivity.premium,
        brandLoyalty: MARKET_SEGMENTS.brandLoyalty.premium
      },
      [MARKET_SEGMENTS.names.PREMIUM_ENTHUSIASTS]: {
        baseSize: MARKET_SEGMENTS.sizes.premium,
        priceElasticity: MARKET_SEGMENTS.priceElasticity.premium,
        optimalPriceRange: [MARKET_SEGMENTS.priceRanges.premium.min, MARKET_SEGMENTS.priceRanges.premium.max],
        techSensitivity: MARKET_SEGMENTS.techSensitivity.premium,
        brandLoyalty: MARKET_SEGMENTS.brandLoyalty.premium
      },
      [MARKET_SEGMENTS.names.ENTERPRISE]: {
        baseSize: MARKET_SEGMENTS.sizes.enterprise,
        priceElasticity: MARKET_SEGMENTS.priceElasticity.enterprise,
        optimalPriceRange: [MARKET_SEGMENTS.priceRanges.enterprise.min, MARKET_SEGMENTS.priceRanges.enterprise.max],
        techSensitivity: MARKET_SEGMENTS.techSensitivity.enterprise,
        brandLoyalty: MARKET_SEGMENTS.brandLoyalty.enterprise
      },
      [MARKET_SEGMENTS.names.ENTERPRISE_USERS]: {
        baseSize: MARKET_SEGMENTS.sizes.enterprise,
        priceElasticity: MARKET_SEGMENTS.priceElasticity.enterprise,
        optimalPriceRange: [MARKET_SEGMENTS.priceRanges.enterprise.min, MARKET_SEGMENTS.priceRanges.enterprise.max],
        techSensitivity: MARKET_SEGMENTS.techSensitivity.enterprise,
        brandLoyalty: MARKET_SEGMENTS.brandLoyalty.enterprise
      }
    };
    
    // Reset lifecycle stages to default values
    this.lifecycleStages = [
      { 
        name: SALES_CONSTANTS.lifecycleStages.LAUNCH, 
        minMonths: 0, 
        maxMonths: SALES_CONSTANTS.lifecycleDurations.launch, 
        salesMultiplierRange: [SALES_CONSTANTS.lifecycleMultipliers.launch.min, SALES_CONSTANTS.lifecycleMultipliers.launch.max] 
      },
      { 
        name: SALES_CONSTANTS.lifecycleStages.GROWTH, 
        minMonths: SALES_CONSTANTS.lifecycleDurations.launch, 
        maxMonths: SALES_CONSTANTS.lifecycleDurations.launch + SALES_CONSTANTS.lifecycleDurations.growth, 
        salesMultiplierRange: [SALES_CONSTANTS.lifecycleMultipliers.growth.min, SALES_CONSTANTS.lifecycleMultipliers.growth.max] 
      },
      { 
        name: SALES_CONSTANTS.lifecycleStages.MATURITY, 
        minMonths: SALES_CONSTANTS.lifecycleDurations.launch + SALES_CONSTANTS.lifecycleDurations.growth, 
        maxMonths: SALES_CONSTANTS.lifecycleDurations.launch + SALES_CONSTANTS.lifecycleDurations.growth + SALES_CONSTANTS.lifecycleDurations.maturity, 
        salesMultiplierRange: [SALES_CONSTANTS.lifecycleMultipliers.maturity.min, SALES_CONSTANTS.lifecycleMultipliers.maturity.max] 
      },
      { 
        name: SALES_CONSTANTS.lifecycleStages.DECLINE, 
        minMonths: SALES_CONSTANTS.lifecycleDurations.launch + SALES_CONSTANTS.lifecycleDurations.growth + SALES_CONSTANTS.lifecycleDurations.maturity, 
        maxMonths: SALES_CONSTANTS.lifecycleDurations.decline, 
        salesMultiplierRange: [SALES_CONSTANTS.lifecycleMultipliers.decline.min, SALES_CONSTANTS.lifecycleMultipliers.decline.max] 
      }
    ];
    
    // Reset economic phase modifiers
    this.economicPhaseModifiers = ECONOMIC_CONSTANTS.phaseModifiers;
    
    // console.log('💰 Sales Calculation Service: Reset complete');
  }
}
